import { Children, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { 
  VerticalStack, 
  HorizontalStack, 
  SectionEyebrow, 
  DescriptionText, 
  MetricBar, 
  AdminShell,
  WorksurfaceHeader
} from '../blocks';
import { DocumentWorkspace } from '../blocks/DocumentWorkspace';
import type { MetricItemProps } from '../blocks';

export interface ManagementRegistryProps {
  /** Root Shell Configuration */
  parentLabel: string;
  activeLabel: string;
  description?: string;
  onBack?: () => void;
  headerActions?: ReactNode;
  
  /** Technical Metadata Section */
  metrics?: MetricItemProps[];
  
  /** Search & Filter Section */
  toolbar?: ReactNode;
  
  /** Content Section */
  rangeLabel?: string;
  emptyState?: ReactNode;
  children: ReactNode;
  pagination?: ReactNode;
  
  /** Loading State */
  isLoading?: boolean;
  loadingLabel?: string;
  
  /** Layout Toggles */
  bodyClassName?: string;
}

/**
 * ManagementRegistry Blueprint
 * 
 * CODIFIED BLUEPRINT for administrative worksurfaces.
 * Enforces the "Operator Command Center" aesthetic:
 * 1. Persistent AdminShell with WorksurfaceHeader (32px top gap)
 * 2. High-signal MetricBar (32px gap to toolbar)
 * 3. Structured Toolbar (16px gap to list)
 * 4. Micro-labeled Registry (12px gap between range and list)
 */
export function ManagementRegistry({
  parentLabel,
  activeLabel,
  description,
  onBack,
  headerActions,
  metrics,
  toolbar,
  rangeLabel,
  emptyState,
  children,
  pagination,
  isLoading,
  loadingLabel = "Loading data...",
  bodyClassName
}: ManagementRegistryProps) {
  return (
    <AdminShell
      title={
        <WorksurfaceHeader 
          parentLabel={parentLabel}
          activeLabel={activeLabel} 
          onBack={onBack}
          description={description}
        />
      }
      actions={headerActions}
      bodyClassName="flex min-h-0 flex-col"
      flat
    >
      <DocumentWorkspace contentClassName={cn('p-3 sm:p-6 3xl:p-12 4k:p-16', bodyClassName)}>
      <VerticalStack gap={6} className="sm:gap-8">
        {metrics && <MetricBar items={metrics} />}
        
        <VerticalStack gap={4} className="sm:gap-6">
          {toolbar}
          
          {isLoading ? (
            <VerticalStack gap={4} className="p-12 items-center">
              <DescriptionText className="animate-pulse font-medium">
                {loadingLabel}
              </DescriptionText>
            </VerticalStack>
          ) : (
            <VerticalStack gap={3}>
              {rangeLabel && (
                <HorizontalStack className="justify-between px-1" wrap>
                  <SectionEyebrow>{rangeLabel}</SectionEyebrow>
                </HorizontalStack>
              )}
              
              {Children.toArray(children).filter(Boolean).length === 0 && emptyState ? (
                <div className="py-12">{emptyState}</div>
              ) : (
                <>
                  {children}
                  {pagination}
                </>
              )}
            </VerticalStack>
          )}
        </VerticalStack>
      </VerticalStack>
      </DocumentWorkspace>
    </AdminShell>
  );
}
